import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, gql, compose } from 'react-apollo';
import { connect } from 'react-redux';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { toastr } from 'react-redux-toastr';
import { injectIntl } from 'react-intl';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import {
  GripVertical,
  Save,
  CheckCircle,
  Info,
  Image,
  Star,
  ThumbsUp,
  Eye,
  MapPin,
  ImageIcon,
  FileText,
  Map,
  Loader2
} from 'lucide-react';

import s from './HomeModulesManagement.css';
import cs from '../../commonStyle.css';
import messages from '../../../locale/messages';

// Icon mapping for modules
const ICON_MAP = {
  Image: Image,
  Star: Star,
  ThumbsUp: ThumbsUp,
  Eye: Eye,
  MapPin: MapPin,
  ImageIcon: ImageIcon,
  Info: FileText,
  Map: Map,
};

class HomeModulesManagement extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      modules: [],
      loading: false,
      success: false,
      hasChanges: false
    };
  }

  componentDidMount() {
    this.loadModulesFromProps();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data && this.props.data && this.props.data.getHomeModulesSettings) {
      this.loadModulesFromProps();
    }
  }

  loadModulesFromProps = () => {
    const { data } = this.props;
    if (data && data.getHomeModulesSettings && data.getHomeModulesSettings.modules) {
      this.setState({
        modules: [...data.getHomeModulesSettings.modules]
      });
    }
  };

  handleDragEnd = (result) => {
    if (!result.destination) return;

    const { modules } = this.state;
    const items = Array.from(modules);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    this.setState({
      modules: updatedItems,
      hasChanges: true,
      success: false
    });
  };

  handleToggle = (moduleId) => {
    const { modules } = this.state;
    const updatedModules = modules.map(mod => {
      if (mod.id === moduleId) {
        return { ...mod, enabled: !mod.enabled };
      }
      return mod;
    });

    this.setState({
      modules: updatedModules,
      hasChanges: true,
      success: false
    });
  };

  handleSave = async () => {
    const { updateHomeModulesSettings } = this.props;
    const { modules } = this.state;

    this.setState({ loading: true, success: false });

    try {
      const response = await updateHomeModulesSettings({
        variables: {
          modules: JSON.stringify(modules)
        }
      });

      if (response.data.updateHomeModulesSettings.status === 'success') {
        this.setState({ success: true, hasChanges: false });
        toastr.success('Success', 'Home modules configuration saved successfully');
      } else {
        toastr.error('Error', 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Save error:', error);
      toastr.error('Error', 'Failed to save configuration');
    }

    this.setState({ loading: false });
  };

  getModuleIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || FileText;
    return IconComponent;
  };

  render() {
    const { title: pageTitle, intl: { formatMessage }, data } = this.props;
    const { modules, loading, success, hasChanges } = this.state;

    if (data && data.loading) {
      return (
        <div className={cx(s.pagecontentWrapper, 'pagecontentWrapperRTL')}>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading modules...</p>
          </div>
        </div>
      );
    }

    return (
      <div className={cx(s.pagecontentWrapper, 'pagecontentWrapperRTL')}>
        <div className={s.headerSection}>
          <div className={s.headerText}>
            <h1>{pageTitle}</h1>
            <p>{formatMessage(messages.destinationsDescription) || 'Drag and drop to reorder modules. Toggle to enable or disable them on the homepage.'}</p>
          </div>
          <button
            className={s.saveButton}
            onClick={this.handleSave}
            disabled={loading || !hasChanges}
          >
            {loading ? (
              <>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                {formatMessage(messages.savingLabel)}
              </>
            ) : (
              <>
                <Save size={18} />
                {formatMessage(messages.saveSettings)}
              </>
            )}
          </button>
        </div>

        {success && (
          <div className={s.successMessage}>
            <CheckCircle size={20} />
            {formatMessage(messages.settingsSavedSuccess)}
          </div>
        )}

        <div className={s.helpText}>
          <Info size={20} />
          <span>
            <strong>Tip:</strong> Drag modules to change their display order on the homepage. 
            Disabled modules will be hidden from visitors but retain their position for when you re-enable them.
          </span>
        </div>

        <div className={s.modulesList}>
          <div className={s.listHeader}>
            <span></span>
            <span>Module</span>
            <span>Position</span>
            <span style={{ textAlign: 'right' }}>Status</span>
          </div>

          <DragDropContext onDragEnd={this.handleDragEnd}>
            <Droppable droppableId="modules">
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {modules.map((module, index) => {
                    const IconComponent = this.getModuleIcon(module.icon);
                    return (
                      <Draggable
                        key={module.id}
                        draggableId={module.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={cx(
                              s.moduleItem,
                              snapshot.isDragging && s.moduleItemDragging,
                              !module.enabled && s.moduleDisabled
                            )}
                          >
                            <div
                              {...provided.dragHandleProps}
                              className={s.dragHandle}
                            >
                              <GripVertical size={20} />
                            </div>

                            <div className={s.moduleInfo}>
                              <div className={cx(
                                s.moduleIcon,
                                module.enabled ? s.moduleIconEnabled : s.moduleIconDisabled
                              )}>
                                <IconComponent size={24} />
                              </div>
                              <div className={s.moduleDetails}>
                                <h3>{module.label}</h3>
                                <p>{module.description}</p>
                              </div>
                            </div>

                            <div className={s.orderBadge}>
                              <span className={cx(
                                s.orderNumber,
                                module.enabled && s.orderNumberEnabled
                              )}>
                                {index + 1}
                              </span>
                            </div>

                            <div className={s.toggleWrapper}>
                              <div
                                className={cx(s.toggle, module.enabled && s.active)}
                                onClick={() => this.handleToggle(module.id)}
                              >
                                <div className={s.toggleSlider}></div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    );
  }
}

const mapState = (state) => ({});

export default compose(
  injectIntl,
  withStyles(s, cs),
  connect(mapState),
  graphql(gql`
    query getHomeModulesSettings {
      getHomeModulesSettings {
        modules {
          id
          name
          label
          description
          enabled
          order
          icon
        }
      }
    }
  `, {
    options: {
      fetchPolicy: 'network-only'
    }
  }),
  graphql(gql`
    mutation updateHomeModulesSettings($modules: String!) {
      updateHomeModulesSettings(modules: $modules) {
        status
      }
    }
  `, {
    name: 'updateHomeModulesSettings'
  })
)(HomeModulesManagement);
